import IconButtonM from "@common/components/button/IconButtonM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { getPrivilegedRole, hasRole } from "@data/utility/ability"
import {
    groupAccountsByFamily,
    selectSessionStoredTokens,
} from "@data/utility/familyUtils"
import { useNavigation } from "@react-navigation/native"
import React, { useRef, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { switchAccount } from "../../data/redux/actions/authTokens"
import {
    setIsParentMode,
    setParentModeFamilyId,
} from "../../data/redux/actions/parentMode"
import { ProductIcon } from "../../student/account/subscription/MySubscriptionScreen"
import ProfileSwitcherM from "../components/account/ProfileSwitcherM"
import AvatarM from "../components/avatar/AvatarM"
import { BadgeM } from "../components/badge/BadgeM"
import {
    PetalCounter,
    SkillSetTitleFull,
    XPCount,
} from "../components/statistics/Counters"
import TextM from "../components/text/TextM"
import { theme } from "../theme/theme"

const StudentMainMenuHeader = (props) => {
    const { studentId, anonymous = false, withGoBack = false } = props

    const navigation = useNavigation()

    const [switchingAccountId, setSwitchingAccountId] = useState(null)

    // Clear loading state once full user data is loaded after switch
    const clearSwitchingState = () => setSwitchingAccountId(null)

    useApiStatus(
        authService.getUser,
        null,
        false,
        clearSwitchingState,
        clearSwitchingState
    )

    function onAvatarPress() {
        navigation.push("account")
    }

    const currentUser = !studentId
        ? props.user
        : props.students?.find((x) => x?.id === parseInt(studentId))

    // Cache stats (petals, XP, title, badge count) during account switch
    // so they don't flash. Avatar uses currentUser directly for immediate update.
    const statsRef = useRef(currentUser)
    if (currentUser?.petals !== undefined && !switchingAccountId) {
        statsRef.current = currentUser
    }
    const user = currentUser
    const statsUser = switchingAccountId ? statsRef.current : currentUser

    const count =
        (statsUser?.profile_completion !== undefined &&
        statsUser.profile_completion < 100
            ? 1
            : 0) +
        (statsUser?.is_verified !== undefined && !statsUser.is_verified ? 1 : 0)

    // Compute family accounts for the profile switcher
    const isStudent = hasRole(props.user, "student")
    let studentFamilyAccounts = []
    let studentFamilyId = null

    if (isStudent && !studentId && props.storedTokens.length > 0) {
        const { familyGroups } = groupAccountsByFamily(
            props.storedTokens,
            props.familyState,
            true
        )
        const currentUserId = props.user?.id
        const userFamily = familyGroups.find((group) =>
            group.accounts.some((account) => account.userId === currentUserId)
        )
        if (userFamily) {
            studentFamilyAccounts = userFamily.accounts.filter(
                (account) => account.roles && account.roles.includes("student")
            )
            studentFamilyId = userFamily.isIndividualGroup
                ? -1
                : userFamily.familyId
        }
    }

    const showProfileSwitcher =
        isStudent && !studentId && studentFamilyAccounts.length > 0

    const selectedStudentAccount = studentFamilyAccounts.find(
        (account) => account.userId === props.user?.id
    )

    const handleStudentAccountSwitch = (account) => {
        if (account.userId === props.user?.id) return
        if (account.token) {
            setSwitchingAccountId(account.userId)
            props.switchAccount(account)
            props.apiRequest(authService.getUser)
        }
    }

    const handleEnterParentMode = () => {
        if (studentFamilyId !== null) {
            props.setParentModeFamilyId(studentFamilyId)
            props.setIsParentMode(true)
        }
    }

    const headerContent = ({ onPress }) => (
        <TouchableOpacity
            style={{
                padding: 2,
            }}
            disabled={!!studentId}
            onPress={onPress}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent:
                        statsUser?.petals !== undefined
                            ? "space-between"
                            : "flex-end",
                    ...(statsUser?.petals !== undefined && { minWidth: 230 }),
                }}
            >
                {statsUser?.petals !== undefined && (
                    <View
                        style={{
                            padding: 2,
                        }}
                    >
                        <PetalCounter
                            count={statsUser?.petals}
                            style={{ height: 46, marginHorizontal: 4 }}
                        />
                    </View>
                )}

                {statsUser?.xp !== undefined && (
                    <XPCount
                        count={statsUser?.xp}
                        style={{ marginHorizontal: 4 }}
                    />
                )}

                <SkillSetTitleFull
                    title={statsUser?.student_title}
                    icon={statsUser?.student_title_icon}
                    skillSetId={statsUser?.title_skill_set_id}
                />

                {!anonymous && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        {user ? (
                            <View>
                                <AvatarM
                                    user={user}
                                    size={studentId ? 30 : 50}
                                />
                                {(switchingAccountId ||
                                    props.logoutRequest ||
                                    props.getStaticDataRequest ||
                                    props.subscribeToNotificationsRequest) && (
                                    <View
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            borderRadius: 25,
                                            backgroundColor:
                                                "rgba(255,255,255,0.5)",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <ActivityIndicator
                                            size="small"
                                            color={theme.colors.primary}
                                        />
                                    </View>
                                )}
                            </View>
                        ) : (
                            <ActivityIndicator
                                size={30}
                                color={theme.colors.primary}
                            />
                        )}
                        {studentId && (
                            <TextM
                                fontWeight="SemiBold"
                                style={{
                                    textAlign: "right",
                                    marginLeft: 4,
                                    maxWidth: 30,
                                }}
                                color={theme.colors.primary}
                                fontSize={9}
                                wrap
                            >
                                {" "}
                                {user?.display_name}
                            </TextM>
                        )}
                        <ProductIcon
                            style={{
                                position: "absolute",
                                right: -4,
                                bottom: 0,
                            }}
                        />
                        {count > 0 &&
                            !switchingAccountId &&
                            getPrivilegedRole(props.user) !== "basic" && (
                                <BadgeM
                                    count={count}
                                    style={{
                                        backgroundColor: theme.colors.error,
                                        color: theme.colors.white,
                                        position: "absolute",
                                        top: -2,
                                        right: -2,
                                    }}
                                />
                            )}
                    </View>
                )}
                {showProfileSwitcher && (
                    <View style={{ marginLeft: 4 }}>
                        <FontAwesome5
                            solid
                            name="caret-down"
                            size={16}
                            color={theme.colors.primary}
                        />
                    </View>
                )}
            </View>
        </TouchableOpacity>
    )

    const renderRightHeaderButton = () => {
        if (showProfileSwitcher) {
            return (
                <ProfileSwitcherM
                    accounts={studentFamilyAccounts}
                    selectedAccount={selectedStudentAccount}
                    onAccountSelect={handleStudentAccountSwitch}
                    onParentSelect={handleEnterParentMode}
                    isParentMode={false}
                    user={props.user}
                    onSettings={() => navigation.push("account")}
                    switchingAccountId={switchingAccountId}
                    loading={props.getUserRequest}
                    renderTrigger={headerContent}
                />
            )
        }

        return headerContent({ onPress: onAvatarPress })
    }

    return (
        <View
            style={{
                width: "100%",
                height: 50,
                flexDirection: "row",
                justifyContent: withGoBack ? "space-between" : "flex-end",
                alignItems: "center",
            }}
        >
            {withGoBack && (
                <IconButtonM
                    icon={"arrow-left"}
                    iconColor={theme.colors.primary}
                    containerColor={theme.colors.transparent}
                    style={{
                        marginRight: 5,
                        zIndex: 1,
                    }}
                    onPress={() => {
                        navigation.goBack()
                    }}
                    size={32}
                />
            )}
            <View
                style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingHorizontal: studentId ? 0 : 10,
                    width: "100%",
                    height: 50,
                }}
            >
                {renderRightHeaderButton()}
            </View>
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        students: state.data.currentUser?.object?.students,
        storedTokens: selectSessionStoredTokens(state),
        familyState: state.data.family || { families: {}, userFamilyMap: {} },
        logoutRequest: state?.api?.logout?.request,
        getStaticDataRequest: state?.api?.getStaticData?.request,
        subscribeToNotificationsRequest:
            state?.api?.subscribeToNotifications?.request,
        getUserRequest: state?.api?.getUser?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    switchAccount,
    setIsParentMode,
    setParentModeFamilyId,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StudentMainMenuHeader)
